import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Rating,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BedIcon from '@mui/icons-material/Bed';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { getHotelProductInfo, getHotelRooms } from '../../../api/endpoints/hotel';
import { HotelFilterPanel, type FiltersState } from './HotelFilterPanel';
import type {
  HotelFacility,
  HotelFilter,
  HotelProductInfoResponse,
  HotelSearchRequest,
  PriceResult,
} from '../../../api/types/hotel';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import type { HotelFlowState } from '../flowConfig';

/** Facility category id -> Turkish box title, per the legacy "Hakkında" layout.
 *  Unrecognized categories (Meals, Internet, Transfer, ...) fall back to their raw
 *  label rather than being dropped. Categories below (beyond the legacy three) are
 *  confirmed via a live region search response (Alexandroupoli, GR). */
const FACILITY_GROUP_LABELS: Record<string, string> = {
  General: 'Otel Özellikleri',
  Rooms: 'Oda Özellikleri',
  'Services and amenities': 'Otel Servisleri',
  Meals: 'Yeme & İçme',
  Internet: 'İnternet',
  Transfer: 'Ulaşım',
  Recreation: 'Aktiviteler',
  Parking: 'Otopark',
  'Pool and beach': 'Havuz & Plaj',
  Sports: 'Spor',
  Pets: 'Evcil Hayvan',
  Accessibility: 'Erişilebilirlik',
  Business: 'İş Olanakları',
  Kids: 'Çocuklar İçin',
  'Beauty and wellness': 'Güzellik & Sağlık',
  'Health and Safety Measures': 'Sağlık & Güvenlik Önlemleri',
  'Languages Spoken': 'Konuşulan Diller',
};

function groupFacilities(facilities: HotelFacility[]): { title: string; items: string[] }[] {
  const groups = new Map<string, string[]>();
  facilities.forEach((facility) => {
    const key = facility.id ?? 'Diğer';
    const label = facility.name ?? facility.id;
    if (!label) return;
    const list = groups.get(key);
    if (list) list.push(label);
    else groups.set(key, [label]);
  });
  // "General"/"Rooms"/"Services and amenities" first (legacy's three boxes), then the rest.
  const priority = Object.keys(FACILITY_GROUP_LABELS);
  const keys = Array.from(groups.keys()).sort((a, b) => {
    const pa = priority.indexOf(a);
    const pb = priority.indexOf(b);
    if (pa === -1 && pb === -1) return a.localeCompare(b);
    if (pa === -1) return 1;
    if (pb === -1) return -1;
    return pa - pb;
  });
  return keys.map((key) => ({ title: FACILITY_GROUP_LABELS[key] ?? key, items: groups.get(key) ?? [] }));
}

/* ------------------------------------------------------------------------ *
 * Grouping — `Hotel/Search` returns a flat list where each `PriceResult` is
 * already one bookable hotel+room+rate combination (see api/types/hotel.ts).
 * Grouped here client-side by hotelId so the UI can present "one card per
 * hotel, one row per room/rate offer", matching the legacy results page.
 * ------------------------------------------------------------------------ */
interface HotelGroup {
  info: PriceResult;
  offers: PriceResult[];
  minPrice: number;
}

function offerPrice(offer: PriceResult): number | undefined {
  return offer.servicePrice ?? offer.arkmanPrice;
}

/** Confirmed via a live response: `roomName`/`boardName`/`adultCount`/`childCount` come
 *  back null/0 at the top level — the real values live only on `rooms[0]`. Exported so
 *  every other hotel step (Reservation/Payment/Document summaries) reads the same
 *  fallback chain instead of the top-level field directly. */
export function offerRoomName(offer: PriceResult): string | undefined {
  return offer.rooms?.[0]?.roomName ?? offer.roomName ?? offer.roomTypeName ?? undefined;
}

export function offerBoardName(offer: PriceResult): string | undefined {
  return offer.rooms?.[0]?.boardName ?? offer.boardName ?? undefined;
}

function offerAdultCount(offer: PriceResult): number {
  return offer.rooms?.[0]?.adt ?? offer.adultCount ?? 0;
}

function offerChildCount(offer: PriceResult): number {
  return offer.rooms?.[0]?.chd ?? offer.childCount ?? 0;
}

/** `offerId` (== `rateGuid` == `rooms[0].roomId`) is the actually-unique per-offer
 *  identifier — `rateId` is shared across every offer of the same hotel, confirmed via
 *  a live response, so it must never be used alone as a React key. */
function offerKey(offer: PriceResult): string {
  return offer.offerId ?? offer.rateGuid ?? offer.rooms?.[0]?.roomId ?? offer.rateId ?? '';
}

/** `acyKomisyon` has been observed empty in every live response so far — its real
 *  format is unconfirmed (see PriceResult comment). Parsed defensively as a bare
 *  percentage (e.g. "10" or "10%") of the offer price; returns null whenever it's
 *  empty or not a parseable number, so the commission line is simply omitted. */
function offerCommission(offer: PriceResult): { rate: string; total: string } | null {
  const raw = offer.acyKomisyon?.trim();
  if (!raw) return null;
  const rate = parseFloat(raw.replace(',', '.').replace('%', ''));
  const price = offerPrice(offer);
  if (!Number.isFinite(rate) || price === undefined) return null;
  const total = (price * rate) / 100;
  return {
    rate: rate.toLocaleString('tr-TR'),
    total: total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  };
}

function groupByHotel(results: PriceResult[]): HotelGroup[] {
  const groups = new Map<string, HotelGroup>();
  results.forEach((offer) => {
    const key = offer.hotelId ?? offer.hotelName ?? JSON.stringify(offer);
    const existing = groups.get(key);
    if (existing) {
      existing.offers.push(offer);
    } else {
      groups.set(key, { info: offer, offers: [offer], minPrice: Infinity });
    }
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    minPrice: Math.min(...group.offers.map((o) => offerPrice(o) ?? Infinity)),
  }));
}

function totalGuests(searchRequest?: HotelSearchRequest) {
  const rooms = searchRequest?.passengersInfo ?? [];
  return {
    rooms: rooms.length,
    adults: rooms.reduce((sum, r) => sum + r.adt, 0),
    children: rooms.reduce((sum, r) => sum + r.child, 0),
  };
}

/* ------------------------------------------------------------------------ *
 * Search summary bar — collapsible recap of the search criteria, mirrors
 * the legacy results page header ("Bölge Otel: ... Giriş Tarihi: ...").
 * ------------------------------------------------------------------------ */
function SearchSummaryBar({ searchRequest }: { searchRequest?: HotelSearchRequest }) {
  const [open, setOpen] = useState(true);
  if (!searchRequest) return null;
  const guests = totalGuests(searchRequest);
  const formatDate = (iso?: string) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '—');

  return (
    <Paper sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
        <Typography variant="subtitle2">Arama Kriterleri</Typography>
        <IconButton size="small" onClick={() => setOpen((v) => !v)} sx={{ color: 'inherit' }}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Typography variant="body2">
            Bölge/Otel: {searchRequest.locationName ?? searchRequest.hotelName ?? '—'} · Giriş Tarihi:{' '}
            {formatDate(searchRequest.startDate)} · Çıkış Tarihi: {formatDate(searchRequest.endDate)} · Döviz
            Cinsi: {searchRequest.currencyType ?? 'TRY'} · Uyruk Kodu: {searchRequest.nationalityType ?? '—'} ·{' '}
            {guests.rooms} Oda, {guests.adults} Yetişkin, {guests.children} Çocuk
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
}

/* ------------------------------------------------------------------------ *
 * Odalar tab — one card per room/rate offer.
 * ------------------------------------------------------------------------ */
function RoomCard({
  offer,
  disabled,
  onSelect,
}: {
  offer: PriceResult;
  disabled: boolean;
  onSelect: () => void;
}) {
  const roomName = offerRoomName(offer);
  const boardName = offerBoardName(offer);
  const price = offerPrice(offer);
  const policyLines = useMemo(
    () => offer.cancellationPolicy?.split(/\r?\n/).map((l) => l.trim()).filter(Boolean) ?? [],
    [offer.cancellationPolicy]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 700 }}>
        {roomName ?? 'Oda'}
      </Typography>
      {boardName && (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.5 }}>
          <RestaurantIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {boardName}
          </Typography>
        </Stack>
      )}

      <Divider sx={{ my: 1.5 }} />

      {policyLines.length > 0 && (
        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          {policyLines.map((line, index) => (
            <Stack key={index} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <InfoIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {line}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {price !== undefined ? `${price} ${offer.currencyType ?? ''}` : '—'}
        </Typography>
        <Button
          variant="contained"
          disabled={disabled || !offer.rateId}
          onClick={onSelect}
        >
          Odayı Seç
        </Button>
      </Stack>
    </Paper>
  );
}

function RoomsList({
  offers,
  disabled,
  onSelect,
}: {
  offers: PriceResult[];
  disabled: boolean;
  onSelect: (offer: PriceResult) => void;
}) {
  return (
    <Grid container spacing={2}>
      {offers.map((offer, index) => (
        <Grid key={offerKey(offer) || index} size={{ xs: 12, sm: 6 }}>
          <RoomCard offer={offer} disabled={disabled} onSelect={() => onSelect(offer)} />
        </Grid>
      ))}
    </Grid>
  );
}

/* ------------------------------------------------------------------------ *
 * Hakkında tab — sourced from `Hotel/GetProductInfo` (fetched by HotelCard when
 * "Detayları Göster" expands the card) once it resolves; falls back to grouping
 * `PriceResult.facilities` by its `id` (see groupFacilities) for instant content
 * before/if that call hasn't returned. GetProductInfo's three lists are relabeled by
 * their CONFIRMED content, not their (misleading) C# property names — see
 * HotelProductInfoResponse's comment in api/types/hotel.ts.
 * ------------------------------------------------------------------------ */
function FacilityBox({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <Grid size={{ xs: 12, sm: 4 }}>
      <Paper variant="outlined">
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: 2, py: 1 }}>
          <Typography variant="subtitle2">{title}</Typography>
        </Box>
        <Stack divider={<Divider />}>
          {items.map((label, index) => (
            <Box key={label || index} sx={{ px: 2, py: 1 }}>
              <Typography variant="body2">{label}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Grid>
  );
}

function toLabels(filters?: HotelFilter[] | null): string[] {
  return (filters ?? []).map((f) => f.filterName ?? f.hotelName).filter((l): l is string => !!l);
}

export function AboutTab({
  info,
  productInfo,
  productInfoLoading,
}: {
  info: PriceResult;
  productInfo?: HotelProductInfoResponse;
  productInfoLoading: boolean;
}) {
  const fallbackGroups = useMemo(() => groupFacilities(info.facilities ?? []), [info.facilities]);

  // GetProductInfo's lists are content-mapped, not name-mapped — see file header.
  const groups = productInfo
    ? [
        { title: 'Otel Özellikleri', items: toLabels(productInfo.hotelPropertyList) },
        { title: 'Oda Özellikleri', items: toLabels(productInfo.hotelServiceList) },
        { title: 'Otel Servisleri', items: toLabels(productInfo.roomPropertyList) },
      ].filter((g) => g.items.length > 0)
    : fallbackGroups;

  const address = productInfo?.address ?? info.address;
  const latitude = productInfo?.latitude ?? info.latitude;
  const longitude = productInfo?.longitude ?? info.longitude;

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        Adres: {[address, info.cityName, info.countryName].filter(Boolean).join(', ') || '—'}
      </Typography>

      {productInfo?.description && <Typography variant="body2">{productInfo.description}</Typography>}

      {latitude !== undefined && longitude !== undefined && (
        <Link
          href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
          target="_blank"
          rel="noopener"
          underline="hover"
        >
          Haritada Göster
        </Link>
      )}

      {productInfoLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {groups.length > 0 ? (
        <Grid container spacing={2}>
          {groups.map((group) => (
            <FacilityBox key={group.title} title={group.title} items={group.items} />
          ))}
        </Grid>
      ) : (
        !productInfoLoading && (
          <Typography variant="body2" color="text.secondary">
            Otel özelliği bilgisi bulunamadı.
          </Typography>
        )
      )}
    </Stack>
  );
}

/* ------------------------------------------------------------------------ *
 * Fotoğraflar tab lightbox — click a thumbnail to open it full-size, then
 * step through the hotel's other photos with the on-screen arrows, the
 * keyboard (←/→/Esc), or by clicking the dimmed backdrop to close.
 * ------------------------------------------------------------------------ */
function PhotoLightbox({
  images,
  index,
  onClose,
  onNavigate,
}: {
  images: string[];
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const open = index !== null;

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') onNavigate(((index as number) + 1) % images.length);
      else if (e.key === 'ArrowLeft') onNavigate(((index as number) - 1 + images.length) % images.length);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, index, images.length, onNavigate, onClose]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box sx={{ position: 'relative', bgcolor: 'common.black', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white', zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>

        {images.length > 1 && (
          <IconButton
            onClick={() => onNavigate(((index as number) - 1 + images.length) % images.length)}
            sx={{ position: 'absolute', left: 8, color: 'common.white', zIndex: 1 }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        )}

        <Box
          component="img"
          src={images[index as number]}
          alt=""
          sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />

        {images.length > 1 && (
          <IconButton
            onClick={() => onNavigate(((index as number) + 1) % images.length)}
            sx={{ position: 'absolute', right: 8, color: 'common.white', zIndex: 1 }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}

        {images.length > 1 && (
          <Typography
            variant="caption"
            sx={{ position: 'absolute', bottom: 8, color: 'common.white', bgcolor: 'rgba(0,0,0,0.5)', px: 1, borderRadius: 1 }}
          >
            {(index as number) + 1} / {images.length}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}

/* ------------------------------------------------------------------------ *
 * One hotel card — image, name, star rating, the cheapest offer's summary
 * (room/board/guests/cancellation/commission), starting price, and the
 * ODALAR / ÖZELLİKLER / FOTOĞRAFLAR tabs.
 * ------------------------------------------------------------------------ */
function HotelCard({
  group,
  searchRequest,
  searchId,
  disabled,
  onSelect,
}: {
  group: HotelGroup;
  searchRequest?: HotelSearchRequest;
  /** From the region search's top-level `HotelFilterResponse.searchId` (stamped onto
   *  `HotelSearchResponse` by searchHotels()) — NOT per-offer `PriceResult.searchId`,
   *  which is unreliable/often null. Required by the `Hotel/GetFiltersFromCollaction`
   *  drill-down to stay in the same server-side search session. */
  searchId?: string;
  disabled: boolean;
  onSelect: (offer: PriceResult) => void;
}) {
  const { info, offers, minPrice } = group;
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState<'rooms' | 'about' | 'gallery'>('rooms');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // "Başlayan fiyatlarla" summary (room/board/cancellation/commission) is sourced
  // from the cheapest offer, matching the starting price shown alongside it.
  const cheapestOffer = offers.find((o) => offerPrice(o) === minPrice) ?? offers[0];

  // Fetched once "Detayları Göster" expands the card (per confirmed HotelProductInfoResponse
  // shape) — feeds the ÖZELLİKLER tab's facility boxes/address and, when present, the
  // FOTOĞRAFLAR tab's images (hotelImageList/roomImageList — richer than the per-offer
  // hotelImageUrl fallback, which is all Hotel/Search itself ever provides).
  const productInfoQuery = useQuery({
    queryKey: ['hotelProductInfo', info.hotelId, info.arkmanHotel],
    queryFn: () => getHotelProductInfo(info.hotelId as string, info.arkmanHotel ?? false),
    enabled: expanded && !!info.hotelId,
    staleTime: 5 * 60 * 1000,
  });
  const productInfo = productInfoQuery.data;

  // Also fetched on expand — `Hotel/GetFiltersFromCollaction` drill-down for THIS
  // hotel's full room/rate list (the region search's `offers` can be a partial/paged
  // slice — see getHotelRooms in api/endpoints/hotel.ts). `rateId` (== hotelId) is the
  // hotel identifier the drill-down keys off; falls back to the region search's own
  // `offers` while loading or if it fails/returns nothing.
  const rateId = cheapestOffer?.rateId ?? info.rateId;
  const roomsQuery = useQuery({
    queryKey: ['hotelRooms', rateId, searchId],
    queryFn: () =>
      getHotelRooms(searchRequest as HotelSearchRequest, {
        rateId: rateId as string,
        hotelName: productInfo?.hotelName ?? info.hotelName,
        searchId,
      }),
    enabled: expanded && !!searchRequest && !!rateId && !!searchId,
    staleTime: 5 * 60 * 1000,
  });
  const roomOffers = roomsQuery.data?.priceResults?.length ? roomsQuery.data.priceResults : offers;

  // `hotelImageList` on `PriceResult` has been observed `null` in every live response
  // so far — every offer instead carries a single `hotelImageUrl`. Prefer
  // GetProductInfo's richer `hotelImageList`/`roomImageList` once loaded; fall back to
  // the distinct `hotelImageUrl`s across this hotel's offers before/without it.
  const images = useMemo(() => {
    const fromProductInfo = [
      ...(productInfo?.hotelImageList ?? []),
      ...(productInfo?.roomImageList ?? []),
    ]
      .map((img) => img.url)
      .filter((u): u is string => !!u);
    if (fromProductInfo.length) return Array.from(new Set(fromProductInfo));
    const fromList = info.hotelImageList?.map((img) => img.url).filter((u): u is string => !!u) ?? [];
    if (fromList.length) return fromList;
    const fromOffers = offers.map((o) => o.hotelImageUrl).filter((u): u is string => !!u);
    return Array.from(new Set(fromOffers));
  }, [productInfo, info.hotelImageList, offers]);
  const thumbnail = info.hotelImageUrl ?? images[0];

  const commission = cheapestOffer ? offerCommission(cheapestOffer) : null;
  const firstPolicyLine = cheapestOffer?.cancellationPolicy?.split(/\r?\n/)[0]?.trim();

  return (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {thumbnail && (
            <Box
              component="img"
              src={thumbnail}
              alt={info.hotelName}
              sx={{ width: { xs: '100%', sm: 220 }, height: { xs: 180, sm: 220 }, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {info.hotelName ?? 'Otel'}
            </Typography>
            {info.star !== undefined && <Rating value={info.star} precision={0.5} size="small" readOnly sx={{ mt: 0.5 }} />}

            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 1 }}>
              <LocationOnIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {[info.cityName, info.countryName].filter(Boolean).join(', ') || info.address || '—'}
              </Typography>
            </Stack>

            {cheapestOffer && (
              <Stack direction="row" spacing={3} sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                {offerRoomName(cheapestOffer) && (
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <BedIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                      {offerRoomName(cheapestOffer)}
                    </Typography>
                  </Stack>
                )}
                {offerBoardName(cheapestOffer) && (
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <RestaurantIcon fontSize="small" color="action" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {offerBoardName(cheapestOffer)}
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <PeopleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {offerAdultCount(cheapestOffer)} Yetişkin, {offerChildCount(cheapestOffer)} Çocuk
                  </Typography>
                </Stack>
              </Stack>
            )}

            {firstPolicyLine && (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 1 }}>
                <InfoIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {firstPolicyLine}
                </Typography>
              </Stack>
            )}

            {commission && (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.5 }}>
                <AttachMoneyIcon fontSize="small" color="action" />
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                  Oran %{commission.rate} Kazanç Toplam {commission.total} TL
                </Typography>
              </Stack>
            )}
          </Box>

          <Stack spacing={1} sx={{ alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'center', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Başlayan fiyatlarla
            </Typography>
            {Number.isFinite(minPrice) && (
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {minPrice} {info.currencyType ?? 'TRY'}
              </Typography>
            )}
            <Button variant="outlined" onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Detayları Gizle' : 'Detayları Göster'}
            </Button>
          </Stack>
        </Stack>

        {expanded && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
              <Tab value="rooms" label="ODALAR" />
              <Tab value="about" label="ÖZELLİKLER" />
              <Tab value="gallery" label="FOTOĞRAFLAR" />
            </Tabs>

            {tab === 'rooms' && (
              <>
                {roomsQuery.isFetching && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}
                <RoomsList offers={roomOffers} disabled={disabled} onSelect={onSelect} />
              </>
            )}

            {tab === 'about' && (
              <AboutTab info={info} productInfo={productInfo} productInfoLoading={productInfoQuery.isFetching} />
            )}

            {tab === 'gallery' && (
              <>
                {images?.length ? (
                  <Grid container spacing={1}>
                    {images.map((url, index) => (
                      <Grid key={index} size={{ xs: 6, sm: 4, md: 3 }}>
                        <Box
                          component="img"
                          src={url}
                          alt=""
                          onClick={() => setLightboxIndex(index)}
                          sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Alert severity="info">Görsel bulunamadı.</Alert>
                )}
                <PhotoLightbox
                  images={images}
                  index={lightboxIndex}
                  onClose={() => setLightboxIndex(null)}
                  onNavigate={setLightboxIndex}
                />
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------------ *
 * Step shell
 * ------------------------------------------------------------------------ */
type SortOption = 'default' | 'priceAsc' | 'priceDesc' | 'starDesc';

export function ResultsStep() {
  const flow = useBookingFlowContext<HotelFlowState>();
  const priceResults = useMemo(() => flow.state.searchResult?.priceResults ?? [], [flow.state.searchResult]);
  // Sunucu-taraflı filtre facet'leri — searchHotels() `HotelSearchResponse.filters`
  // altına damgalıyor (yıldız grupları / fiyat sınırları / tesis listeleri).
  const serverFacets = flow.state.searchResult?.filters;

  const priceBounds = useMemo<[number, number]>(() => {
    const prices = priceResults.map(offerPrice).filter((p): p is number => typeof p === 'number');
    const clientMin = prices.length ? Math.min(...prices) : undefined;
    const clientMax = prices.length ? Math.max(...prices) : undefined;
    // Sunucu `minPrice`/`maxPrice` mantıklıysa (max > min > 0) istemci sınırlarıyla birleştir.
    const sMin = serverFacets?.minPrice;
    const sMax = serverFacets?.maxPrice;
    const useServer = typeof sMin === 'number' && typeof sMax === 'number' && sMax > sMin && sMax > 0;
    const mins = [clientMin, useServer ? sMin : undefined].filter((n): n is number => typeof n === 'number');
    const maxs = [clientMax, useServer ? sMax : undefined].filter((n): n is number => typeof n === 'number');
    if (!mins.length || !maxs.length) return [0, 0];
    return [Math.min(...mins), Math.max(...maxs)];
  }, [priceResults, serverFacets]);

  const starOptions = useMemo<[number, number][]>(() => {
    const map = new Map<number, number>();
    groupByHotel(priceResults).forEach(({ info }) => {
      if (info.star !== undefined) map.set(info.star, (map.get(info.star) ?? 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [priceResults]);

  const boardOptions = useMemo<[string, number][]>(() => {
    const map = new Map<string, number>();
    priceResults.forEach((o) => {
      const board = offerBoardName(o);
      if (board) map.set(board, (map.get(board) ?? 0) + 1);
    });
    return Array.from(map.entries());
  }, [priceResults]);

  const facilityOptions = useMemo<[string, number][]>(() => {
    const map = new Map<string, number>();
    priceResults.forEach((o) => (o.facilities ?? []).forEach((f) => {
      if (f.name) map.set(f.name, (map.get(f.name) ?? 0) + 1);
    }));
    return Array.from(map.entries());
  }, [priceResults]);

  const [filters, setFilters] = useState<FiltersState>({
    hotelName: '',
    region: '',
    subRegion: '',
    refund: 'all',
    stars: new Set(),
    boards: new Set(),
    facilities: new Set(),
    priceRange: priceBounds,
  });
  const [sortBy, setSortBy] = useState<SortOption>('default');

  // Reset the price-range selection whenever a new search result comes in.
  useEffect(() => {
    setFilters((f) => ({ ...f, priceRange: priceBounds }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBounds[0], priceBounds[1]]);

  const filteredOffers = useMemo(() => {
    return priceResults.filter((o) => {
      if (filters.hotelName && !(o.hotelName ?? '').toLowerCase().includes(filters.hotelName.toLowerCase())) {
        return false;
      }
      if (filters.region && !(o.cityName ?? '').toLowerCase().includes(filters.region.toLowerCase())) {
        return false;
      }
      if (filters.subRegion && !(o.address ?? '').toLowerCase().includes(filters.subRegion.toLowerCase())) {
        return false;
      }
      if (filters.stars.size && (o.star === undefined || !filters.stars.has(o.star))) return false;
      if (filters.boards.size) {
        const board = offerBoardName(o);
        if (!board || !filters.boards.has(board)) return false;
      }
      if (filters.facilities.size) {
        const names = new Set((o.facilities ?? []).map((f) => f.name).filter(Boolean));
        if (!Array.from(filters.facilities).some((f) => names.has(f))) return false;
      }
      if (filters.refund === 'withPolicy' && !o.cancellationPolicy) return false;
      if (filters.refund === 'noPolicy' && !!o.cancellationPolicy) return false;
      const price = offerPrice(o);
      if (price !== undefined && (price < filters.priceRange[0] || price > filters.priceRange[1])) return false;
      return true;
    });
  }, [priceResults, filters]);

  const hotels = useMemo(() => {
    const groups = groupByHotel(filteredOffers);
    const sorted = [...groups];
    if (sortBy === 'priceAsc') sorted.sort((a, b) => a.minPrice - b.minPrice);
    else if (sortBy === 'priceDesc') sorted.sort((a, b) => b.minPrice - a.minPrice);
    else if (sortBy === 'starDesc') sorted.sort((a, b) => (b.info.star ?? 0) - (a.info.star ?? 0));
    return sorted;
  }, [filteredOffers, sortBy]);

  if (flow.status === 'running') {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (!priceResults.length) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            Arama kriterlerinize uygun otel bulunamadı. Farklı bir lokasyon veya tarih deneyin.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <SearchSummaryBar searchRequest={flow.state.searchRequest} />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <HotelFilterPanel
            filters={filters}
            setFilters={setFilters}
            priceBounds={priceBounds}
            starOptions={starOptions}
            boardOptions={boardOptions}
            facilityOptions={facilityOptions}
            serverFacets={serverFacets}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2">{hotels.length} adet konaklayabileceğiniz otel bulundu</Typography>
              <Chip size="small" color="primary" label="Otel Listesi" />
            </Stack>
            <TextField
              select
              size="small"
              label="Sırala"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="default">Seçiniz</MenuItem>
              <MenuItem value="priceAsc">Fiyat (Artan)</MenuItem>
              <MenuItem value="priceDesc">Fiyat (Azalan)</MenuItem>
              <MenuItem value="starDesc">Yıldız (Azalan)</MenuItem>
            </TextField>
          </Stack>

          {!hotels.length ? (
            <Alert severity="info">Filtrelere uygun otel bulunamadı.</Alert>
          ) : (
            <Stack spacing={2}>
              {hotels.map((group, index) => (
                <HotelCard
                  key={group.info.hotelId ?? group.info.hotelName ?? index}
                  group={group}
                  searchRequest={flow.state.searchRequest}
                  searchId={flow.state.searchResult?.searchId}
                  disabled={flow.status === 'running'}
                  onSelect={(offer) => flow.runStep({ selectedOffer: offer })}
                />
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
